import {
  AsphaltCoreSheet,
  AsphaltLot,
  AsphaltReportItem,
} from './instances/asphalt'
import {
  ConcreteCylinderIASheet,
  ConcretePlacementCoreSheet,
  ConcreteTestSet,
  ConcreteTestSetCylinders,
  ConcreteTestSetPlacements,
  ConcreteReportItem,
} from './instances/concrete'
import {
  ControlPoint,
  Inspection,
  PunchList,
  DailyInspectionReport,
} from './instances/dailyInspection'
import { DensityLot } from './instances/density'
import { ConstructionAdminSubmittal } from './instances/cas'
import { listStringify } from '../../utils/listStringify'
import { NamedComplexEnum } from '../../utils/complexEnum/NamedComplexEnum'

export type ReportItem =
  | AsphaltReportItem
  | typeof DensityLot
  | typeof ConstructionAdminSubmittal
  | ConcreteReportItem
  | DailyInspectionReport
type Name = ReportItem['name']
type ID = ReportItem['id']
class ReportEnum extends NamedComplexEnum<'Report', ReportItem, Name, ID> {
  readonly typename = 'Report'
  readonly AsphaltCoreSheet = AsphaltCoreSheet
  readonly AsphaltLot = AsphaltLot
  readonly ConcreteCylinderIASheet = ConcreteCylinderIASheet
  readonly ConcretePlacementCoreSheet = ConcretePlacementCoreSheet
  readonly ConcreteTestSet = ConcreteTestSet
  readonly ConcreteTestSetCylinders = ConcreteTestSetCylinders
  readonly ConcreteTestSetPlacements = ConcreteTestSetPlacements
  readonly ControlPoint = ControlPoint
  readonly Inspection = Inspection
  readonly PunchList = PunchList
  readonly DensityLot = DensityLot
  readonly ConstructionAdminSubmittal = ConstructionAdminSubmittal
  get values() {
    return [
      this.AsphaltCoreSheet,
      this.AsphaltLot,
      this.ConcreteCylinderIASheet,
      this.ConcretePlacementCoreSheet,
      this.ConcreteTestSet,
      this.ConcreteTestSetCylinders,
      this.ConcreteTestSetPlacements,
      this.ControlPoint,
      this.Inspection,
      this.PunchList,
      this.DensityLot,
      this.ConstructionAdminSubmittal,
    ]
  }
  /**
   * validates the supplied {@link searchParams} and returns them with their
   * associated ReportItem
   *
   * @param searchParams a {@link URLSearchParams} object
   */
  fromRequest(endpoint: string, searchParams: URLSearchParams) {
    const queryParameter = searchParams.get('report')
    if (!queryParameter) throw new Error('report query parameter is undefined')
    const report = this.values.find(
      (report) =>
        queryParameter === report.queryParameter && endpoint === report.endpoint
    )
    if (!report)
      throw new Error(
        `Invalid report: ${queryParameter}. Please use ${listStringify({
          items: this.values.map(({ queryParameter }) => queryParameter),
          or: true,
        })}`
      )
    const globalId = searchParams.get('globalId')
    const reportId = report.id
    if (
      reportId === this.ConstructionAdminSubmittal.id ||
      reportId === this.ConcreteTestSet.id
    ) {
      const driveItemIds = searchParams.get('files')?.split(',') ?? []
      const skip = searchParams.get('skipSharePointUpload')
      if (skip !== 'true' && skip !== 'false')
        throw new Error('skipSharePointUpload must be true or false')
      const skipSharePointUpload = skip === 'true'
      if (reportId === this.ConstructionAdminSubmittal.id) {
        const recordId = searchParams.get('recordId')
        if (!recordId)
          throw new Error(
            `recordId is a required parameter for ${this.ConstructionAdminSubmittal.name}`
          )
        return { reportId, recordId, driveItemIds, skipSharePointUpload }
      } else if (reportId === this.ConcreteTestSet.id) {
        if (!globalId)
          throw new Error(
            `globalId is a required parameter for ${this.ConcreteTestSet.name}`
          )
        return { reportId, globalId, driveItemIds, skipSharePointUpload }
      }
    }
    if (!globalId)
      throw new Error(`globalId is a required parameter for ${report.name}`)
    return { reportId, globalId }
  }
  /**
   * returns a callback to be used later, based on the supplied report. Works best
   * when used inside a loop where the report is the main variable of the report
   *
   * You must define a callback for every report
   *
   * Every callback needs to return the supplied return type
   *
   * Every callback should use the supplied arguments
   *
   * @example
   * type ArgsType = { one: string two: number three: Date }
   *
   * const stringCallback = (report: Report, args: ArgsType): string => {
   *  const callback = reportCallback<string, ArgsType>(report.id, {
   *    AsphaltLot: (args) => asphaltLotWork(args),
   *    ...
   *    Inspection: inspectionWork,
   *  })
   *  return callback(args)
   * }
   */
  callback<ReturnType = unknown, ArgsType = never>(
    id: string,
    callbacks: Callbacks<ReturnType, ArgsType>
  ) {
    const found = this.fromIdOrFail(id)
    if (found === this.AsphaltLot) return callbacks.AsphaltLot
    else if (found === this.AsphaltCoreSheet) return callbacks.AsphaltCoreSheet
    else if (found === this.ConcreteCylinderIASheet)
      return callbacks.ConcreteCylinderIASheet
    else if (found === this.ConcretePlacementCoreSheet)
      return callbacks.ConcretePlacementAndCoreSheet
    else if (found === this.ConcreteTestSet) return callbacks.ConcreteTestSet
    else if (found === this.ControlPoint) return callbacks.ControlPoint
    else if (found === this.DensityLot) return callbacks.DensityLot
    else if (found === this.Inspection) return callbacks.Inspection
    else if (found === this.PunchList) return callbacks.PunchList
    else if (found === this.ConstructionAdminSubmittal)
      return callbacks.ConstructionAdminSubmittal
    else throw new Error(`invalid report id: ${id}`)
  }
  /**
   * @see {@link filter}
   */
  filterByClassType(classType: string) {
    return this.filter((report) => report.classType === classType)
  }
  /**
   * @see {@link filter}
   */
  filterByClassTypes(classTypes: string[]) {
    return this.filter(({ classType }) => classTypes.includes(classType))
  }
  /**
   * @see {@link filter}
   */
  filterByEndpoint(endpoint: string) {
    return this.filter((report) => report.endpoint === endpoint)
  }
  /**
   * a unique list of `Report.classType` values
   */
  get classTypes() {
    return [...new Set(this.map(({ classType }) => classType))]
  }
  /**
   * a unique list of `Report.endpoint` values
   */
  get endpoints() {
    return [...new Set(this.map(({ endpoint }) => endpoint))]
  }
  /**
   * reports that are accessible in the client
   */
  get clientReports() {
    return this.filter(({ accessibleInClient }) => accessibleInClient)
  }
  /**
   * converts a list of ids into the names of the associated {@link ReportItem}
   * @param ids
   * @throws if any id does not resolve into a valid {@link ReportItem}
   */
  toNames(ids: string[]) {
    return ids.map((id) => this.fromIdOrFail(id).name)
  }
}

export const Report = new ReportEnum()

type Callback<ReturnType, Interface> = (args: Interface) => ReturnType

interface Callbacks<T = unknown, U = never> {
  /**
   * logic to run when the report is the Asphalt Core Sheet
   */
  AsphaltCoreSheet: Callback<T, U>
  /**
   * logic to run when the report is the Asphalt Lot Report
   */
  AsphaltLot: Callback<T, U>
  /**
   * logic to run when the report is the Concrete Cylinder and IA Sheet
   */
  ConcreteCylinderIASheet: Callback<T, U>
  /**
   * logic to run when the report is the Concrete Placement and Core Sheet
   */
  ConcretePlacementAndCoreSheet: Callback<T, U>
  /**
   * logic to run when the report is the Concrete Test Set Packet
   */
  ConcreteTestSet: Callback<T, U>
  /**
   * logic to run when the report is the Control Point Report
   */
  ControlPoint: Callback<T, U>
  /**
   * logic to run when the report is the Density Lot Report
   */
  DensityLot: Callback<T, U>
  /**
   * logic to run when the report is the Inspection Report
   */
  Inspection: Callback<T, U>
  /**
   * logic to run when the report is the Punch List Report
   */
  PunchList: Callback<T, U>
  /**
   * logic to run when the report is the Construction & Admin Submittal Packet
   */
  ConstructionAdminSubmittal: Callback<T, U>
}
